import "~/vendor.css";
import "~/index.css";

import { datadogRum } from "@datadog/browser-rum";
import { autoAnimatePlugin } from "@formkit/auto-animate/vue";
import * as Sentry from "@sentry/vue";
import { LicenseManager } from "ag-grid-enterprise";
import FloatingVue from "floating-vue";
import { UAParser } from "ua-parser-js";
import { createApp, nextTick, watch } from "vue";
import type { Router } from "vue-router";

import actions from "~/actions";
import { backendOld, requestManager } from "~/api";
import App from "~/App.vue";
import { initGlobalPopper } from "~/components/globalPopper";
import { initMarkdown } from "~/components/text/markdownRules";
import { $createAttachmentNode } from "~/components/text/nodes/AttachmentNode";
import { $createDartLinkNode } from "~/components/text/nodes/DartLinkNode";
import { $createEntityNode } from "~/components/text/nodes/EntityNode";
import { TRANSFORMERS } from "~/components/text/transformers";
import { checkNavigationRules, finalizeNavigation, initRouterForUtils } from "~/router/common";
import router from "~/router/router";
import { EntityName, EventKind, InboxFilter } from "~/shared/enums";
import { pinia, useAppStore, useDataStore, useEnvironmentStore, usePageStore, useUserStore } from "~/stores";
import ApiPlugin from "~/stores/plugins/ApiPlugin";
import RouterPlugin from "~/stores/plugins/RouterPlugin";
import vuetify from "~/stores/plugins/VuetifyPlugin";
import { openPathInDesktop, run } from "~/utils/common";
import { callDesktop, listenDesktop } from "~/utils/desktop";
import { initExceptionClient, normalizeSentryEvent } from "~/utils/exceptionClient";
import registerGlobalEventListeners from "~/utils/globalEventManager";
import registerInputModalityListeners from "~/utils/inputModality";
import { COLLECT_ALL_DATA_KEY, lsGet, lsListen, USER_DUID_LOCAL_STORAGE_KEY } from "~/utils/localStorageManager";
import { recordDuration, startMemoryMonitoring } from "~/utils/observability";
import { provideComponents } from "~/utils/provide";

import DesktopSearch from "./components/DesktopSearch.vue";
import { AG_GRID_LICENSE_KEY } from "./constants/app";
import AutoWidthDirective from "./utils/AutoWidthDirective";
import DisabledGlobalShortcutsDirective from "./utils/DisabledGlobalShortcutsDirective";
import prepareAgGrid from "./utils/prepareAgGrid";
import ScrollSyncDirective from "./utils/ScrollSyncDirective";

(() => {
  // Open app as desktop search box
  const isDesktopApp = UAParser(navigator.userAgent).browser.name === "Electron";
  if (isDesktopApp && window.location.pathname === "/desktop-window-search") {
    const app = createApp(DesktopSearch);
    app.mount("#app");
    return;
  }

  const app = createApp(App);

  // Set up core elements of app
  pinia.use(RouterPlugin);
  pinia.use(ApiPlugin);
  app.use(pinia);

  // Add components
  app.use(autoAnimatePlugin);
  app.directive("disabled-global-shortcuts", DisabledGlobalShortcutsDirective);
  app.directive("auto-width", AutoWidthDirective);
  app.directive("scroll-sync", ScrollSyncDirective);
  app.use(FloatingVue, {
    themes: {
      "tooltip-light": { $extend: "tooltip" },
      "tooltip-dark": { $extend: "tooltip" },
      "tooltip-arrowless-light": { $extend: "tooltip" },
      "tooltip-arrowless-dark": { $extend: "tooltip" },
      "tutorial-light": { $extend: "tooltip" },
      "tutorial-dark": { $extend: "tooltip" },
      "tutorial-light-ai": { $extend: "tooltip" },
      "tutorial-dark-ai": { $extend: "tooltip" },
      "dropdown-light": { $extend: "dropdown" },
      "dropdown-dark": { $extend: "dropdown" },
    },
  });
  app.use(vuetify);

  // Print errors to console
  // eslint-disable-next-line no-console
  app.config.errorHandler = console.error;
  app.config.warnHandler = (msg, vm, trace) => {
    // TODO popper remove when we switch
    if (msg.startsWith("Invalid prop") && trace.startsWith("at <DropdownMenu>")) {
      return;
    }
    // TODO popper remove when we switch
    if (
      msg.includes("Runtime directive used on component with non-element root node") &&
      trace.includes("GlobalPopper")
    ) {
      return;
    }
    // eslint-disable-next-line no-console
    console.warn(msg, trace);
  };

  const appStore = useAppStore();
  const dataStore = useDataStore();
  const environmentStore = useEnvironmentStore();
  const pageStore = usePageStore();
  const userStore = useUserStore();

  initMarkdown(
    (color, isBackground) => userStore.addToRecentColorStack(color, isBackground),
    (url: string, alt: string) => {
      const duid = dataStore.createAttachmentFromUrl(url, alt);
      return $createAttachmentNode(duid, false);
    },
    $createEntityNode,
    (link, kind, duid, label) => $createDartLinkNode(link, kind as EntityName, duid, label),
    TRANSFORMERS
  );

  provideComponents(app);
  initGlobalPopper();

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.register("/serviceWorker.js");
  }

  environmentStore.setEnvironment(window.location);
  initExceptionClient(environmentStore.isLocal);
  pageStore.setManualConsoleLogging(pageStore.isMobile);
  pageStore.updateTheme();

  // Warn about inherited attributes
  if (!environmentStore.isProd) {
    const ALLOWED_COMPONENTS = new Set(["GlobalPopper"]);
    const ALLOWED_INHERITED_ATTRS = new Set([
      "class",
      "style",
      "dataTestid",
      "data-testid",
      "dataToolbar",
      "data-toolbar",
      "dataGroup",
      "data-group",
      "onClick",
      "onKeydown",
      "onContextmenu",
      // Used by Tailwind's on variant
      "dataOn",
      "data-on",
      // TODO Remove these two below, used by TaskClickWrapper.vue
      "oncontextmenu",
      "onclick",
      // Used by Chips.vue
      "show-text",
      // Provided by MultiselectDropdown.vue
      "onSelect",
      // Used by GlobalPopper.vue
      "dataGlobalPopperIgnore",
      "data-global-popper-ignore",
    ]);
    app.mixin({
      created() {
        const inheritedAttrsEntries = Object.entries(this.$attrs).filter(
          ([key, value]) => value !== undefined && !ALLOWED_INHERITED_ATTRS.has(key)
        );

        if (
          Object.keys(inheritedAttrsEntries).length === 0 ||
          this.$options.__file === undefined ||
          this.$options.__name === undefined ||
          ALLOWED_COMPONENTS.has(this.$options.__name)
        ) {
          return;
        }

        // eslint-disable-next-line no-console
        console.warn(
          "Inherited attributes",
          inheritedAttrsEntries.map(([key, value]) => `${key}=${value}`).join(", "),
          `are not supported in the ${this.$options.__name} (${this.$options.__file}) component`
        );
      },
    });
  }

  lsListen(USER_DUID_LOCAL_STORAGE_KEY, (newDuid) => {
    if (newDuid === userStore.duid) {
      return;
    }
    // TODO just update user rather than hard refreshing whole app
    window.location.reload();
  });

  const collectAllData = lsGet(COLLECT_ALL_DATA_KEY) === "true";

  const initSentry = (appInner: typeof app, innerRouter: Router) => {
    if (environmentStore.isLocal) {
      return;
    }

    Sentry.init({
      app: appInner,
      dsn: "https://d7e102a310f442f296aad9df905d1c30@o1178417.ingest.sentry.io/6290355",
      tunnel: "/api/v0/tracking/sentry",
      transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
      release: environmentStore.backendEnvironment?.hash,
      environment: environmentStore.name,
      tracePropagationTargets: [
        /^\/api/,
        /https:\/\/app\.dartai\.com\/api/,
        /https:\/\/stag\.dartai\.com\/api/,
        "localhost",
      ],
      integrations: [
        Sentry.browserTracingIntegration({ router: innerRouter }),
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: false,
          blockAllMedia: false,
        }),
        Sentry.captureConsoleIntegration({
          levels: ["warning", "error"],
        }),
      ],
      attachStacktrace: true,
      tracesSampleRate: collectAllData ? 1.0 : 0.1,
      replaysSessionSampleRate: collectAllData ? 1.0 : 0.2,
      replaysOnErrorSampleRate: 1.0,
      beforeSend: (event) => normalizeSentryEvent(event, environmentStore.backendEnvironment?.hash),
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const initDatadog = () => {
    if (environmentStore.isLocal) {
      return;
    }

    datadogRum.init({
      applicationId: "b7a5ca08-e39c-4481-b242-1af238c7baa7",
      clientToken: "pub255232d4b468b83f00f2054ea5279c20",
      site: "us3.datadoghq.com",
      proxy: `${environmentStore.urlBase}/api/v0/tracking/datadog`,
      service: "dart",
      env: environmentStore.name,
      version: environmentStore.backendEnvironment?.hash,
      defaultPrivacyLevel: "allow",
      sessionReplaySampleRate: collectAllData ? 100 : 20,
    });
    datadogRum.startSessionReplayRecording();
  };

  LicenseManager.setLicenseKey(AG_GRID_LICENSE_KEY);
  prepareAgGrid();

  if (environmentStore.isLocal) {
    // eslint-disable-next-line no-console
    const originalConsoleInfo = console.info;
    // eslint-disable-next-line no-console
    console.info = (...args) => {
      const firstArg = args[0];
      if (typeof firstArg === "string" && firstArg.includes("Download the React DevTools")) {
        return;
      }
      originalConsoleInfo(...args);
    };
  }

  registerGlobalEventListeners(userStore);
  registerInputModalityListeners();

  initSentry(app, router);
  // TODO monitoring unification reenable or remove
  // initDatadog();

  app.use(router);
  initRouterForUtils(router);

  app.mount("#app");

  watch(
    () => pageStore.userDataLoaded,
    async (userDataLoaded) => {
      if (!userDataLoaded) {
        return;
      }
      await nextTick();

      const attributes = {
        authenticated: pageStore.isLoggedIn,
        route: String(router.currentRoute.value.name ?? "unknown"),
      };
      recordDuration("app.time_to_usable", 0, attributes);
      recordDuration("route.content_ready", 0, { ...attributes, navigation_kind: "initial" });
      startMemoryMonitoring();
    },
    { flush: "post", once: true }
  );

  const initUserData = async () => {
    const { data } = await backendOld.getUserData();
    actions.app.setUserData(data);
  };

  const initData = async () => {
    await requestManager.init();

    await router.isReady();

    backendOld.createEvent(EventKind.LOAD_APP, {
      path: router.currentRoute.value.fullPath,
      agent: pageStore.agentToDict,
    });

    const initialPromises = [backendOld.setCsrf()];
    initialPromises.push(initUserData());
    await Promise.all(initialPromises);

    actions.app.watchForIdle();
    appStore.connectWs(actions.websockets.getManager());

    // Prefetch unread notifications if not already in inbox
    if (pageStore.isLoggedIn && !pageStore.isPublic && router.currentRoute.value.name !== "inbox") {
      dataStore.loadPaginatedNotifications(InboxFilter.UNREAD);
    }

    window.addEventListener("beforeunload", (event: BeforeUnloadEvent) => {
      if (!appStore.getWorking()) {
        return undefined;
      }

      const message = "Changes you made may not be saved.";
      // eslint-disable-next-line no-param-reassign
      event.returnValue = message;
      return message;
    });

    const currRoute = router.currentRoute.value;
    const navGoal = checkNavigationRules(currRoute);
    if (navGoal === true) {
      pageStore.userDataLoaded = true;
      return;
    }
    run(async () => {
      await router.push(navGoal);
      pageStore.userDataLoaded = true;
    });
    const destRoute = router
      .getRoutes()
      .find((e) => ("name" in navGoal && e.name === navGoal.name) || ("path" in navGoal && e.path === navGoal.path));
    if (!destRoute) {
      return;
    }
    finalizeNavigation(destRoute.meta);
  };

  initData();

  const redirectToNativeMaybe = async () => {
    if (!pageStore.isNativeApp && userStore.openInNativeApp) {
      const currentPath = window.location.href.replace(window.location.origin, "");
      if (currentPath !== "/") {
        const openedInNative = await openPathInDesktop(currentPath);
        pageStore.redirectedToNative = openedInNative;
      }
    }
  };

  // TODO Figure out the right place to put this. It gets overwritten by other route changes. https://github.com/its-dart/dart/pull/1284#discussion_r1178626949 https://app.dartai.com/t/-ySdjFb9i7wD
  // oxlint-disable-next-line dart/no-set-timeout
  setTimeout(() => {
    redirectToNativeMaybe();
  }, 100);

  callDesktop(() => {
    listenDesktop(DESKTOP.onChangeRoute, (path) => {
      router.push(path);
    });

    router.beforeEach((to) => {
      DESKTOP.onRouteChanged(to.path);
    });
  });
})();
